import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, Users } from 'lucide-react';
import {
  participantService,
  ParticipantAvatar,
  SessionState,
  TabletopCastContact,
  tabletopChatThreadKey,
} from '../../../services/participant.service';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  avatarName: string;
  timestamp: Date;
}

interface Props {
  sessionId: string;
  state: SessionState;
}

export function ChatTab({ sessionId, state }: Props) {
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>('guide');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Once a cast persona's thread has been loaded into local state, that
  // local state is the source of truth for the rest of this component's
  // life — the parent polls every few seconds, and a message sent seconds
  // ago may not have round-tripped into `state.tabletop.chatThreads` yet by
  // the time the player switches tabs and back. Falling back to the poll
  // snapshot on every switch would then reload a stale, shorter thread and
  // make the just-completed exchange appear to vanish. Keyed by personaId;
  // only ever read/written for cast selections (see the load effect below).
  const castThreadCacheRef = useRef<Record<string, Message[]>>({});

  const guideName = state.guide?.name || 'Ce-Ce';
  const tabletop = state.tabletop;
  const castContacts: TabletopCastContact[] = tabletop?.castContacts ?? [];

  // Non-tabletop sessions keep the original picker exactly (generic session
  // avatars); tabletop sessions build the picker from castContacts instead,
  // per plan 05 — a persona's avatarId there is its personaId, which the
  // chat route recognises and branches on server-side.
  const allAvatars: Array<{ id: string; name: string; imageUrl?: string; primaryColor?: string; organisation?: string; category?: string }> =
    tabletop
      ? [{ id: 'guide', name: guideName }, ...castContacts.map(c => ({ id: c.personaId, name: c.displayName, organisation: c.organisation, category: c.category }))]
      : [{ id: 'guide', name: guideName }, ...state.avatars];
  const selectedAvatar = allAvatars.find(a => a.id === selectedAvatarId) ?? allAvatars[0];

  const mySeatPersonaId = tabletop?.seat?.personaId;
  const isCastSelection = !!tabletop && selectedAvatarId !== 'guide';
  // 'running' only — matches the backend's own gate in sendPersonaMessage
  // (tabletop-persona-chat.service.ts), which mirrors speakAs's convention
  // that a held run is a facilitator-paused state, not just "not complete
  // yet". A 'held' run must show as not-live here too, or the input looks
  // enabled right up until the participant's send comes back refused.
  const tabletopLive = tabletop ? tabletop.runStatus === 'running' : true;
  const disabledReason = !isCastSelection
    ? null
    : !mySeatPersonaId
      ? 'Claim a seat in the exercise to message the cast.'
      : !tabletopLive
        ? tabletop?.runStatus === 'complete' ? 'This exercise has ended.' : 'The exercise is not currently live.'
        : null;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load the existing thread whenever the player switches avatars, so a cast
  // conversation survives a tab switch / reload rather than looking like the
  // persona forgot everything (plan 05) — and switching to the guide (or to
  // a cast contact with no thread) resets to a clean slate instead of
  // leaving the previous cast persona's messages on screen under the wrong
  // header. Once a persona's thread has a local cache entry, that cache
  // (kept current by sendMessage below) wins over the poll snapshot — see
  // castThreadCacheRef's own comment for why.
  useEffect(() => {
    if (!tabletop) return; // non-tabletop sessions keep their pre-existing (shared, unreset) history
    if (!isCastSelection || !mySeatPersonaId) {
      setMessages([]);
      return;
    }
    const cached = castThreadCacheRef.current[selectedAvatarId];
    if (cached) {
      setMessages(cached);
      return;
    }
    const threadKey = tabletopChatThreadKey(mySeatPersonaId, selectedAvatarId);
    const thread = tabletop.chatThreads[threadKey];
    const loaded = (thread?.messages ?? []).map((m, i) => ({
      id: `${threadKey}-${i}`,
      role: (m.speaker === 'human' ? 'user' : 'assistant') as Message['role'],
      text: m.text,
      avatarName: m.speaker === 'human' ? 'You' : (thread?.displayName ?? selectedAvatar?.name ?? ''),
      timestamp: new Date(),
    }));
    castThreadCacheRef.current[selectedAvatarId] = loaded;
    setMessages(loaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAvatarId, mySeatPersonaId]);

  // Appends to `messages` and, for a cast selection, mirrors the same append
  // into castThreadCacheRef so the local cache never falls behind what this
  // tab has actually shown — that cache is what the load effect above trusts
  // over a possibly-lagging poll snapshot.
  function appendMessage(msg: Message) {
    setMessages(prev => {
      const next = [...prev, msg];
      if (isCastSelection) castThreadCacheRef.current[selectedAvatarId] = next;
      return next;
    });
  }

  async function sendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || sending || disabledReason) return;
    const text = input.trim();
    setInput('');
    appendMessage({
      id: Date.now().toString(),
      role: 'user',
      text,
      avatarName: 'You',
      timestamp: new Date(),
    });
    setSending(true);
    try {
      const result = await participantService.sendChat(
        sessionId,
        text,
        selectedAvatarId === 'guide' ? undefined : selectedAvatarId
      );
      appendMessage({
        id: result.messageId,
        role: 'assistant',
        text: result.response,
        avatarName: selectedAvatar?.name ?? guideName,
        timestamp: new Date(),
      });
    } catch (err: any) {
      appendMessage({
        id: Date.now().toString(),
        role: 'assistant',
        // Cast-persona refusals (no live run, muted, over the rate limit) carry
        // a deliberate, in-character or plain-English message from the backend
        // (see tabletop-persona-chat.service.ts) — show it directly rather than
        // masking it behind a generic error.
        text: (isCastSelection && err?.message) || 'Something went wrong — please try again.',
        avatarName: selectedAvatar?.name ?? guideName,
        timestamp: new Date(),
      });
    } finally {
      setSending(false);
    }
  }

  function renderAvatarIcon(avatar: { id: string; name: string; imageUrl?: string; primaryColor?: string }) {
    if (avatar.id === 'guide') {
      return <Sparkles size={12} />;
    }
    if ((avatar as ParticipantAvatar).imageUrl) {
      return <img src={(avatar as ParticipantAvatar).imageUrl} alt="" className="w-4 h-4 rounded-full object-cover" />;
    }
    return (
      <div
        className="w-4 h-4 rounded-full flex-shrink-0"
        style={{ backgroundColor: (avatar as ParticipantAvatar).primaryColor || '#334155' }}
      />
    );
  }

  function renderAvatarPill(avatar: { id: string; name: string; imageUrl?: string; primaryColor?: string; organisation?: string; category?: string }) {
    const showMeta = tabletop && avatar.id !== 'guide' && (avatar.organisation || avatar.category);
    return (
      <button
        key={avatar.id}
        onClick={() => setSelectedAvatarId(avatar.id)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium flex-shrink-0 transition-all text-left ${
          selectedAvatarId === avatar.id
            ? 'bg-primary text-navy'
            : 'bg-[var(--pt-surface-strong)] text-[var(--pt-text-dim)]'
        }`}
      >
        {renderAvatarIcon(avatar)}
        <span className="flex flex-col leading-tight">
          <span>{avatar.name}</span>
          {showMeta && (
            <span className={`text-[10px] font-normal ${selectedAvatarId === avatar.id ? 'text-navy/70' : 'text-[var(--pt-text-dimmer)]'}`}>
              {[avatar.organisation, avatar.category?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Avatar selector */}
      {tabletop ? (
        <div className="flex flex-col gap-2 px-4 py-3 border-b border-[var(--pt-border)] flex-shrink-0">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--pt-text-dimmer)] mb-1.5 flex items-center gap-1">
              <Users size={10} /> Your cast
            </p>
            <div className="flex gap-2 overflow-x-auto scrollbar-none">
              {castContacts.length === 0
                ? <p className="text-xs text-[var(--pt-text-dimmer)] py-1.5">No cast reachable yet.</p>
                : allAvatars.filter(a => a.id !== 'guide').map(avatar => renderAvatarPill(avatar))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--pt-text-dimmer)] mb-1.5">Session</p>
            <div className="flex gap-2 overflow-x-auto scrollbar-none">
              {allAvatars.filter(a => a.id === 'guide').map(avatar => renderAvatarPill(avatar))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-none border-b border-[var(--pt-border)] flex-shrink-0">
          {allAvatars.map(avatar => renderAvatarPill(avatar))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            {selectedAvatarId === 'guide' ? (
              <>
                <div className="w-12 h-12 bg-primary/20 rounded-2xl flex items-center justify-center mb-3">
                  <Sparkles size={20} className="text-primary" />
                </div>
                <p className="text-[var(--pt-text)] font-semibold mb-1">Chat with {guideName}</p>
                <p className="text-[var(--pt-text-dimmer)] text-sm max-w-56">
                  Ask about the session, what's been discussed, or what's coming next.
                </p>
              </>
            ) : (
              <p className="text-[var(--pt-text-dimmer)] text-sm">Start a conversation with {selectedAvatar?.name}</p>
            )}
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-primary text-navy font-medium'
                : 'bg-[var(--pt-surface-strong)] text-[var(--pt-text)]'
            }`}>
              {msg.role === 'assistant' && (
                <p className="text-xs text-[var(--pt-text-dimmer)] mb-1 font-medium">{msg.avatarName}</p>
              )}
              {msg.role === 'assistant' ? (
                <div className="prose prose-invert prose-sm max-w-none
                  prose-p:my-1 prose-li:my-0
                  prose-table:border-collapse
                  prose-td:border prose-td:border-[var(--pt-border-strong)] prose-td:px-2 prose-td:py-1
                  prose-th:border prose-th:border-[var(--pt-border-strong)] prose-th:px-2 prose-th:py-1 prose-th:bg-[var(--pt-surface-strong)]">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                </div>
              ) : msg.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[var(--pt-surface-strong)] rounded-2xl px-4 py-3">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-[var(--pt-text-dimmer)] rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-[var(--pt-text-dimmer)] rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-[var(--pt-text-dimmer)] rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="flex-shrink-0 px-4 pb-4 pt-2 border-t border-[var(--pt-border)]">
        {disabledReason && (
          <p className="text-xs text-[var(--pt-text-dimmer)] mb-2">{disabledReason}</p>
        )}
        <div className="flex gap-2 items-end">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={!!disabledReason}
            placeholder={disabledReason || `Message ${selectedAvatar?.name ?? guideName}…`}
            className="flex-1 bg-[var(--pt-surface-strong)] border border-[var(--pt-border-strong)] rounded-xl px-4 py-3 text-[var(--pt-text)] placeholder-[var(--pt-text-dimmer)] text-sm focus:outline-none focus:border-primary transition-all resize-none disabled:opacity-50"
            maxLength={1000}
          />
          <button
            type="submit"
            disabled={!input.trim() || sending || !!disabledReason}
            className="w-11 h-11 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-40 active:scale-90 transition-all"
          >
            <Send size={16} className="text-navy" />
          </button>
        </div>
      </form>
    </div>
  );
}
